import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from "libphonenumber-js/min";
import {
  getExampleNumber as getMobileExampleNumber,
  validatePhoneNumberLength as validateMobilePhoneNumberLength,
} from "libphonenumber-js/mobile";
import mobileExamples from "libphonenumber-js/mobile/examples";

const DEFAULT_COUNTRY = "IN";
const PHONE_CONTACT_TYPES = new Set(["mobile", "whatsapp", "parent_mobile"]);
const REGION_DISPLAY_NAMES =
  typeof Intl !== "undefined" && typeof Intl.DisplayNames === "function"
    ? new Intl.DisplayNames(["en"], { type: "region" })
    : null;

const PHONE_COUNTRY_OPTIONS = buildPhoneCountryOptions();
const PHONE_COUNTRY_OPTION_BY_CODE = new Map(
  PHONE_COUNTRY_OPTIONS.map((option) => [option.country, option])
);
const PHONE_COUNTRY_MAX_LENGTH_CACHE = new Map();

export function isIndianPhoneContactType(type) {
  return PHONE_CONTACT_TYPES.has(String(type || "").trim().toLowerCase());
}

export function getIndianPhoneInputValue(value) {
  return toStoredIndianPhoneValue(value);
}

export function validateIndianPhoneValue(value) {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";

  const parts = parseStoredPhoneParts(rawValue);
  if (!parts.nationalNumber) return "";

  const parsed = parsePhoneNumberFromString(parts.display || rawValue, parts.country);
  return parsed?.isValid() ? "" : "Enter a valid phone number for the selected country code.";
}

export function normalizeIndianPhone(value) {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";

  const parts = parseStoredPhoneParts(rawValue);
  const parsed = parsePhoneNumberFromString(parts.display || rawValue, parts.country);
  if (parsed?.isValid()) {
    return String(parsed.number || "").replace(/^\+/, "");
  }

  const fallbackDigits = `${parts.callingCode}${parts.nationalNumber}`.replace(/\D/g, "");
  return fallbackDigits || rawValue.replace(/\D/g, "");
}

export function formatIndianPhoneDisplay(value) {
  return toStoredIndianPhoneValue(value);
}

export function toStoredIndianPhoneValue(value) {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";

  const parts = parseStoredPhoneParts(rawValue);
  if (!parts.nationalNumber) return rawValue;

  const parsed = parsePhoneNumberFromString(parts.display || rawValue, parts.country);
  if (parsed?.isValid()) {
    return formatStoredPhoneValue(parsed.country || parts.country, parsed.nationalNumber || parts.nationalNumber);
  }

  return formatStoredPhoneValue(parts.country, parts.nationalNumber);
}

export function getContactDisplayValue(contact) {
  const type = String(contact?.contact_type || contact?.type || "").trim().toLowerCase();
  const value = contact?.contact_value || "";
  return isIndianPhoneContactType(type) ? formatIndianPhoneDisplay(value) : String(value || "").trim();
}

export function getContactValueFieldLabel(type) {
  const normalized = String(type || "").trim().toLowerCase();
  if (normalized === "email") return "Email";
  if (normalized === "whatsapp") return "WhatsApp Number";
  if (normalized === "parent_mobile") return "Parent Mobile Number";
  if (normalized === "mobile") return "Phone Number";
  if (normalized === "other") return "Other Contact";
  return "Value";
}

export function IndianPhoneInput({
  value,
  onChange,
  inputStyle,
  selectStyle,
  wrapperStyle,
  selectWrapStyle,
  placeholder = "Phone number",
}) {
  const [selectedCountry, setSelectedCountry] = useState(() =>
    parseStoredPhoneParts(value, DEFAULT_COUNTRY, { inferCountryFromValue: true }).country
  );
  const parsedValue = useMemo(
    () => parseStoredPhoneParts(value, selectedCountry || DEFAULT_COUNTRY, { lockCountryToFallback: true }),
    [selectedCountry, value]
  );
  const [nationalNumber, setNationalNumber] = useState(parsedValue.nationalNumber);
  const lastEmittedValueRef = useRef(String(value || "").trim());
  const maxNationalNumberLength = useMemo(
    () => getCountryNationalNumberMaxLength(selectedCountry || DEFAULT_COUNTRY),
    [selectedCountry]
  );
  const digitCountLabel = `${nationalNumber.length}/${maxNationalNumberLength} digits`;

  useEffect(() => {
    setNationalNumber(parsedValue.nationalNumber);
  }, [parsedValue.nationalNumber]);

  return (
    <div
      style={{
        display: "grid",
        gap: 6,
        ...wrapperStyle,
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "140px 1fr",
          gap: 12,
          alignItems: "stretch",
        }}
      >
        <div style={{ position: "relative", ...selectWrapStyle }}>
          <select
            value={selectedCountry}
            style={{
              width: "100%",
              ...selectStyle,
            }}
            onChange={(event) => {
              const nextCountry = String(event.target.value || DEFAULT_COUNTRY);
              const nextMaxLength = getCountryNationalNumberMaxLength(nextCountry);
              const trimmedNationalNumber = String(nationalNumber || "").slice(0, nextMaxLength);
              const nextStoredValue = formatStoredPhoneValue(nextCountry, trimmedNationalNumber);
              setSelectedCountry(nextCountry);
              setNationalNumber(trimmedNationalNumber);
              lastEmittedValueRef.current = nextStoredValue;
              onChange?.(nextStoredValue);
            }}
          >
            {PHONE_COUNTRY_OPTIONS.map((option) => (
              <option key={option.country} value={option.country}>
                {option.shortLabel}
              </option>
            ))}
          </select>
        </div>

        <input
          type="tel"
          inputMode="numeric"
          pattern="[0-9]*"
          value={nationalNumber}
          placeholder={placeholder}
          style={inputStyle}
          onChange={(event) => {
            const nextValue = String(event.target.value || "").replace(/\D/g, "").slice(0, maxNationalNumberLength);
            const nextStoredValue = formatStoredPhoneValue(selectedCountry, nextValue);
            setNationalNumber(nextValue);
            lastEmittedValueRef.current = nextStoredValue;
            onChange?.(nextStoredValue);
          }}
          maxLength={maxNationalNumberLength}
        />
      </div>

      <div style={styles.digitCount}>{digitCountLabel}</div>
    </div>
  );
}

function buildPhoneCountryOptions() {
  return getCountries()
    .map((country) => {
      const callingCode = safeGetCountryCallingCode(country);
      const countryName = REGION_DISPLAY_NAMES?.of(country) || country;
      return {
        country,
        countryName,
        callingCode,
        dialCode: `+${callingCode}`,
        shortLabel: `${country} +${callingCode}`,
      };
    })
    .filter((option) => option.callingCode)
    .sort((left, right) => {
      if (left.country === DEFAULT_COUNTRY) return -1;
      if (right.country === DEFAULT_COUNTRY) return 1;
      return left.countryName.localeCompare(right.countryName) || left.country.localeCompare(right.country);
    });
}

function safeGetCountryCallingCode(country) {
  try {
    return getCountryCallingCode(country);
  } catch {
    return "";
  }
}

function getCountryOption(country) {
  return PHONE_COUNTRY_OPTION_BY_CODE.get(country) || PHONE_COUNTRY_OPTION_BY_CODE.get(DEFAULT_COUNTRY) || {
    country: DEFAULT_COUNTRY,
    countryName: DEFAULT_COUNTRY,
    callingCode: "91",
    dialCode: "+91",
    shortLabel: "IN +91",
  };
}

function parseStoredPhoneParts(value, fallbackCountry = DEFAULT_COUNTRY, options = {}) {
  const fallbackOption = getCountryOption(fallbackCountry);
  const { inferCountryFromValue = false, lockCountryToFallback = false } = options;
  const rawValue = String(value || "").trim();
  if (!rawValue) {
    return {
      country: fallbackOption.country,
      callingCode: fallbackOption.callingCode,
      nationalNumber: "",
      display: "",
    };
  }

  const parsedRaw = parsePhoneNumberFromString(rawValue, fallbackOption.country);
  if (parsedRaw?.countryCallingCode) {
    const parsedCallingCode = String(parsedRaw.countryCallingCode || "");
    const country = inferCountryFromValue && !lockCountryToFallback
      ? parsedRaw.country || fallbackOption.country
      : fallbackOption.country;
    const option = getCountryOption(country);
    return {
      country,
      callingCode: parsedCallingCode,
      nationalNumber: parsedRaw.nationalNumber || "",
      display: formatStoredPhoneValue(country, parsedRaw.nationalNumber || ""),
      shortLabel: option.shortLabel,
    };
  }

  const digits = rawValue.replace(/\D/g, "");
  if (!digits) {
    return {
      country: fallbackOption.country,
      callingCode: fallbackOption.callingCode,
      nationalNumber: "",
      display: "",
    };
  }

  const parsedDigits = parsePhoneNumberFromString(`+${digits}`);
  if (parsedDigits?.countryCallingCode) {
    const parsedCallingCode = String(parsedDigits.countryCallingCode || "");
    const country = inferCountryFromValue && !lockCountryToFallback
      ? parsedDigits.country || fallbackOption.country
      : fallbackOption.country;
    return {
      country,
      callingCode: parsedCallingCode,
      nationalNumber: parsedDigits.nationalNumber || "",
      display: formatStoredPhoneValue(country, parsedDigits.nationalNumber || ""),
    };
  }

  const fallbackNationalNumber = stripDefaultCountryCode(fallbackOption.country, digits, fallbackOption.callingCode);
  return {
    country: fallbackOption.country,
    callingCode: fallbackOption.callingCode,
    nationalNumber: fallbackNationalNumber,
    display: formatStoredPhoneValue(fallbackOption.country, fallbackNationalNumber),
  };
}

function stripDefaultCountryCode(country, digits, callingCode) {
  if (country === "IN") {
    if (digits.length === 12 && digits.startsWith("91")) return digits.slice(2);
    if (digits.length === 11 && digits.startsWith("0")) return digits.slice(1);
  }

  if (digits.startsWith(callingCode) && digits.length > callingCode.length) {
    return digits.slice(callingCode.length);
  }

  return digits;
}

function formatStoredPhoneValue(country, nationalNumber) {
  const digits = String(nationalNumber || "").replace(/\D/g, "");
  if (!digits) return "";
  const option = getCountryOption(country);
  return `${option.dialCode} ${digits}`.trim();
}

function getCountryNationalNumberMaxLength(country) {
  const normalizedCountry = String(country || DEFAULT_COUNTRY).toUpperCase();
  if (PHONE_COUNTRY_MAX_LENGTH_CACHE.has(normalizedCountry)) {
    return PHONE_COUNTRY_MAX_LENGTH_CACHE.get(normalizedCountry);
  }

  const mobileExample = getMobileExampleNumber(normalizedCountry, mobileExamples);
  if (mobileExample?.nationalNumber) {
    const exampleLength = String(mobileExample.nationalNumber).length;
    PHONE_COUNTRY_MAX_LENGTH_CACHE.set(normalizedCountry, exampleLength);
    return exampleLength;
  }

  let maxLength = 15;
  for (let length = 1; length <= 17; length += 1) {
    const testNumber = "9".repeat(length);
    const result = validateMobilePhoneNumberLength(testNumber, normalizedCountry);
    if (result === "TOO_LONG") {
      maxLength = Math.max(1, length - 1);
      break;
    }
  }

  PHONE_COUNTRY_MAX_LENGTH_CACHE.set(normalizedCountry, maxLength);
  return maxLength;
}

const styles = {
  digitCount: {
    justifySelf: "end",
    fontSize: 12,
    color: "#7f8795",
    paddingRight: 2,
  },
};
